<template>
  <fullscreen-overlay-frame :title="$t('invited-user-card.edit-release-period').toString()"
                            icon="manage_accounts"
                            color="primary"
                            centered
                            closable
                            @close="toggleDialog">

    <template v-slot:content>
      <v-form ref="form-restriction-period" v-model="periodRestrictionValid">
        <div>
          <v-icon color="primary" class="mr-1">person</v-icon>
          {{ userInfo.userFirstname }} {{ userInfo.userLastname }}
        </div>

        <v-checkbox v-model="restrictPeriod"
                    class="mt-8"
                    :label="$t('invite-user-dialog.restrict-period.label')"/>
        <v-row class="mt-2">
          <v-col cols="12" sm="6">
            <date-time-picker v-model="userInfo.userValidFrom"
                              :label="$t('invite-user-dialog.valid-from.label').toString()"
                              :disabled="!restrictPeriod"
                              @input="triggerFormValidation"/>
          </v-col>
          <v-col cols="12" sm="6">
            <date-time-picker v-model="userInfo.userValidTo"
                              :label="$t('invite-user-dialog.valid-to.label').toString()"
                              :rules="rules.periodRestriction"
                              :disabled="!restrictPeriod"
                              @input="triggerFormValidation"/>
          </v-col>
        </v-row>
      </v-form>
    </template>
    <template v-slot:actions>
      <v-btn depressed large
             color="primary"
             class="font-weight-bold action-button"
             @click="updateUserPermissions"
             :disabled="!periodRestrictionValid">
        {{ $t('app.save') }}
      </v-btn>
    </template>

  </fullscreen-overlay-frame>
</template>

<script>

import FullscreenOverlayFrame from '@/templates/dialogs/FullscreenOverlayFrame'
import DateTimePicker from '@/templates/components/DateTimePicker'
import moment from 'moment'
import EventBus from '@/plugins/lib/EventBus'

export default {
  name: 'UserPermissionDialog',

  components: {
    DateTimePicker,
    FullscreenOverlayFrame,
  },

  props: ['data'],

  data: function () {
    // Date formats
    const parseIncomingFormat = 'DD.MM.YYYY HH:mm'
    const standardFormat = 'YYYY-MM-DD HH:mm'

    const formatIncomingDate = (dateString) => {
      if (!dateString){
        return null
      }
      const parsed = moment.utc(dateString, parseIncomingFormat, true)
      return parsed.isValid() ? parsed.format(standardFormat) : null
    }

    const initialValidFrom = formatIncomingDate(this.data?.userValidFrom);
    const initialValidTo = formatIncomingDate(this.data?.userValidTo);

    return {
      restrictPeriod: !!(initialValidFrom || initialValidTo),
      periodRestrictionValid: false,
      userInfo: {
        userId: this.data?.userId,
        userValidFrom: initialValidFrom,
        userValidTo: initialValidTo,
        userFirstname: this.data?.userFirstname,
        userLastname: this.data?.userLastname,
        userEmail: this.data?.userEmail
      },
      rules: {
        periodRestriction: [v => this.validatePeriod(v)],
      },
    }
  },

  methods: {
    /**
     * Validates the user's period restriction input.
     * Ensures the 'To' date is not before the 'From' date.
     * Expects input and userInfo dates in 'YYYY-MM-DD HH:mm' format.
     * @param {String | null} input - The current value of the date picker.
     * @returns {Boolean | String} - True if valid, error message string if invalid.
     */
    validatePeriod(input) {
      // Not restricting, so it is valid
      if (!this.restrictPeriod) {
        return true
      }

      // Only compare if both 'From' date and the 'To' date input exist
      if (this.userInfo.userValidFrom && input) {
        let parseFormat = 'YYYY-MM-DD HH:mm'
        let validFrom = moment.utc(this.userInfo.userValidFrom, parseFormat, true)
        let validTo = moment.utc(input, parseFormat, true)

        // Ensure both dates were parsed correctly before comparing
        if (validFrom.isValid() && validTo.isValid()) {
          let result = validTo.isSameOrAfter(validFrom)
          // Return true if valid, otherwise return the translated error message
          return result || this.$t('app.rules.period-restriction-invalid')
        } else {
          console.warn('Date parsing failed during validation!')
          // If the dates can't be parsed, the input is invalid
          return this.$t('app.rules.period-restriction-invalid')
        }
      }

      // If restriction is enabled but one date is missing, the period rule itself passes
      return true
    },

    /**
     * Triggers validation on the form, specifically useful after the 'from' date changes to re-validate the 'to' date.
     */
    triggerFormValidation() {
      // Use $nextTick to ensure the model (userInfo.userValidFrom) has updated
      this.$nextTick(() => {
        if (this.$refs['form-restriction-period']) {
          // Validate the entire form, which will re-run the rule on the 'To' field
          this.$refs['form-restriction-period'].validate()
        }
      })
    },

    /**
     * Updates the user permission dates.
     */
    updateUserPermissions() {
      // Validate the form before submitting
      if (this.$refs['form-restriction-period'].validate()) {
        let payload = {
          userId: this.userInfo.userId,
        }

        // Set the dates only if restriction is enabled, otherwise send nulls (TODO)
        if (this.restrictPeriod) {
          payload.validFrom = this.userInfo.userValidFrom
          payload.validTo = this.userInfo.userValidTo
        } else {
          payload.validFrom = null
          payload.validTo = null
        }

        this.$rhRequest.sendPost({
                                   endpoint: 'permission/edit-invited-user',
                                   data: payload,
                                 }, () => {
          EventBus.$emit('update-data')
          this.$root.bisatoast.success({
                                         message: this.$t('change-invited-user-data-dialog.save.success'),
                                         showCloseBtn: true,
                                       })
          this.toggleDialog()
        }, () => {
          this.$root.bisatoast.error({message: this.$t('app.generic-error'), showCloseBtn: true})
          this.toggleDialog()
        })
      } else {
        this.$root.bisatoast.error({message: this.$t('app.rules.period-restriction-invalid'), showCloseBtn: true})
      }
    },

    /**
     * Closes the dialog.
     */
    toggleDialog() {
      this.$root.bisadialog.toggle('userPermission')
    },

  },

  mounted() {
    // Validate the form once mounted to set the initial button state correctly
    this.triggerFormValidation()
  },
}

</script>